import { Controller } from "@hotwired/stimulus"
import { parseGIF, decompressFrames } from 'gifuct-js'
import { ALLOWED_IMAGE_TYPES, isAllowedImageType } from '../constants/file_types'

export default class extends Controller {
  static targets = ["basePhoto", "targetContainer", "targetPlaceholder", "baseFileName"]
  static values = {
    maxFiles: { type: Number, default: 10 },
    maxFileSize: { type: Number, default: 100 * 1024 * 1024 } // 100MB in bytes
  }

  connect() {
    this.files = new Set()
    this.fileMetadata = new Map()
    this.bindDragAndDrop()
  }

  disconnect() {
    this.unbindDragAndDrop()
  }

  bindDragAndDrop() {
    this.handleDragOver = this.handleDragOver.bind(this)
    this.handleDrop = this.handleDrop.bind(this)
    this.handleDragEnter = this.handleDragEnter.bind(this)
    this.handleDragLeave = this.handleDragLeave.bind(this)
    this.handleDragStart = this.handleDragStart.bind(this)
    
    // Add listeners to both base photo and target media dropzones
    this.basePhotoTarget.addEventListener("dragover", this.handleDragOver)
    this.basePhotoTarget.addEventListener("drop", this.handleDrop)
    this.basePhotoTarget.addEventListener("dragenter", this.handleDragEnter)
    this.basePhotoTarget.addEventListener("dragleave", this.handleDragLeave)
    this.basePhotoTarget.addEventListener("dragstart", this.handleDragStart)
    
    const targetDropzone = this.targetContainerTarget.parentElement
    targetDropzone.addEventListener("dragover", this.handleDragOver)
    targetDropzone.addEventListener("drop", this.handleDrop)
    targetDropzone.addEventListener("dragenter", this.handleDragEnter)
    targetDropzone.addEventListener("dragleave", this.handleDragLeave)
    targetDropzone.addEventListener("dragstart", this.handleDragStart)
  }

  unbindDragAndDrop() {
    if (!this.handleDragOver || !this.handleDrop) return

    this.basePhotoTarget.removeEventListener("dragover", this.handleDragOver)
    this.basePhotoTarget.removeEventListener("drop", this.handleDrop)
    this.basePhotoTarget.removeEventListener("dragenter", this.handleDragEnter)
    this.basePhotoTarget.removeEventListener("dragleave", this.handleDragLeave)
    this.basePhotoTarget.removeEventListener("dragstart", this.handleDragStart)
    
    const targetDropzone = this.targetContainerTarget.parentElement
    if (targetDropzone) {
      targetDropzone.removeEventListener("dragover", this.handleDragOver)
      targetDropzone.removeEventListener("drop", this.handleDrop)
      targetDropzone.removeEventListener("dragenter", this.handleDragEnter)
      targetDropzone.removeEventListener("dragleave", this.handleDragLeave)
      targetDropzone.removeEventListener("dragstart", this.handleDragStart)
    }
  }

  handleDragOver(e) {
    e.preventDefault()
    e.stopPropagation()
  }

  handleDrop(e) {
    e.preventDefault()
    e.stopPropagation()
    
    const files = Array.from(e.dataTransfer.files)
    if (!files.length) return

    // Determine which dropzone received the drop
    if (e.currentTarget === this.basePhotoTarget && files.length === 1) {
      this.handleBasePhotoDrop(files[0])
    } else {
      this.handleTargetMediaDrop(files)
    }
  }

  handleBasePhotoDrop(file) {
    // Check file type for base photo
    if (!isAllowedImageType(file.type)) {
      this.showError('Base photo must be an image file (JPEG, PNG, or WebP). Videos and GIFs should be uploaded as target media.')
      // Clear any existing preview
      this.resetBasePhoto()
      return
    }

    // Check file size limit
    if (file.size > this.maxFileSizeValue) {
      this.showError(`File "${file.name}" exceeds the maximum size limit of 100MB. Please upload a smaller file or reduce the file size.`)
      this.resetBasePhoto()
      return
    }
    
    // Clear any existing errors when valid file is dropped
    this.clearErrors()
    
    // Create a new FileList-like object
    const dataTransfer = new DataTransfer()
    dataTransfer.items.add(file)
    
    // Update the file input
    const fileInput = this.basePhotoTarget.querySelector('input[type="file"]')
    fileInput.files = dataTransfer.files
    
    // Trigger the load event
    this.loadBasePhoto({ target: fileInput })
  }

  async handleTargetMediaDrop(files) {
    const fileInput = this.targetContainerTarget.parentElement.querySelector('input[type="file"]')
    
    // Check total files limit before proceeding
    if (this.files.size + files.length > this.maxFilesValue) {
      this.showError(`You can only upload up to ${this.maxFilesValue} files`)
      return
    }
    
    // Check file size limit
    for (const file of files) {
      if (file.size > this.maxFileSizeValue) {
        this.showError(`File "${file.name}" exceeds the maximum size limit of 100MB. Please upload a smaller file or reduce the file size.`)
        return
      }
    }

    // Create a new DataTransfer for the combined set
    const dataTransfer = new DataTransfer()

    // Re-add existing files from the input
    Array.from(fileInput.files).forEach(file => dataTransfer.items.add(file))

    // Add newly dropped files
    files.forEach(file => dataTransfer.items.add(file))

    // Update the file input with the combined set
    fileInput.files = dataTransfer.files
    
    // Process each new file and gather metadata
    for (const file of files) {
      if (!this.files.has(file)) {
        this.files.add(file)
        await this.processFileMetadata(file)
        this.createPreviewCard(file)
        // Dispatch file type for each file
        this.dispatchFileType(file.type)
      }
    }

    this.updatePlaceholder()
    await this.dispatchCostCalculation()
  }

  loadBasePhoto(event) {
    const file = event.target.files[0]
    if (!file) return

    // Validate file type
    if (!isAllowedImageType(file.type)) {
      this.showError('Base photo must be an image file (JPEG, PNG, or WebP). Videos and GIFs should be uploaded as target media.')
      event.target.value = ''
      this.resetBasePhoto()
      return
    }

    // Clear any existing errors when valid file is selected
    this.clearErrors()

    this.baseFileNameTarget.textContent = file.name
    this.baseFileNameTarget.style.display = "block"
    this.loadPreview(file, this.basePhotoTarget)
  }

  async loadTargetMedia(event) {
    const newFiles = Array.from(event.target.files)
    const fileInput = event.target
    
    if (this.files.size + newFiles.length > this.maxFilesValue) {
      this.showError(`You can only upload up to ${this.maxFilesValue} files`)
      return
    }
    
    // Check file size limit
    for (const file of newFiles) {
      if (file.size > this.maxFileSizeValue) {
        this.showError(`File "${file.name}" exceeds the maximum size limit of 100MB. Please upload a smaller file or reduce the file size.`)
        // Reset the file input to clear the selection
        fileInput.value = ''
        return
      }
    }

    // Create a new DataTransfer for the combined set
    const dataTransfer = new DataTransfer()

    // Re-add existing files from the Set (this.files)
    Array.from(this.files).forEach(file => dataTransfer.items.add(file))

    // Process and add new files
    for (const file of newFiles) {
      if (!this.files.has(file)) {
        this.files.add(file)
        await this.processFileMetadata(file)
        this.createPreviewCard(file)
        dataTransfer.items.add(file)
        // Dispatch file type for each file
        this.dispatchFileType(file.type)
      }
    }

    // Update the file input with the combined set
    fileInput.files = dataTransfer.files

    this.updatePlaceholder()
    await this.dispatchCostCalculation()
  }

  async processFileMetadata(file) {
    if (file.type === "image/gif") {
      const duration = await this.getGifDuration(file)
      this.fileMetadata.set(file.name, { duration })
    } else if (file.type.startsWith("video/")) {
      const duration = await this.getVideoDuration(file)
      this.fileMetadata.set(file.name, { duration })
    }
  }

  /**
   * Updated GIF duration logic.
   * 1. Parse the GIF data
   * 2. Sum the delays for one full loop
   * 3. If we detect suspiciously large or infinite loops, fallback to a practical 0.1s/frame approach
   */
  async getGifDuration(file) {
    return new Promise((resolve) => {
      const reader = new FileReader()
      
      reader.onload = async (e) => {
        try {
          const buffer = e.target.result
          const gif = parseGIF(buffer)
          const frames = decompressFrames(gif, true)

          // Get duration in seconds (convert from ms)
          const durationMs = frames.reduce((acc, frame) => {
            return acc + (frame.delay || 10)
          }, 0)
          const durationSeconds = Number((durationMs / 1000).toFixed(2))
          
          resolve(durationSeconds)
        } catch (error) {
          resolve(3) // Keep fallback for completely corrupt files
        }
      }
      
      reader.readAsArrayBuffer(file)
    })
  }

  getVideoDuration(file) {
    return new Promise((resolve) => {
      const video = document.createElement("video")
      video.preload = "metadata"
      
      video.onloadedmetadata = () => {
        const durationInSeconds = Number(video.duration.toFixed(2))
        resolve(durationInSeconds)
        URL.revokeObjectURL(video.src)
      }
      
      video.src = URL.createObjectURL(file)
    })
  }

  removeFile(event) {
    const card = event.target.closest(".preview-card")
    const fileName = card.dataset.fileName
    
    // Clear the file from our Set
    this.files.forEach(file => {
      if (file.name === fileName) {
        this.files.delete(file)
        this.fileMetadata.delete(fileName)
      }
    })

    // Clear the file input value
    const fileInput = this.targetContainerTarget.parentElement.querySelector('input[type="file"]')
    if (fileInput) {
      // Create a new DataTransfer with remaining files
      const dataTransfer = new DataTransfer()
      this.files.forEach(file => dataTransfer.items.add(file))
      fileInput.files = dataTransfer.files
    }
    
    card.remove()
    this.updatePlaceholder()
    this.dispatchCostCalculation()
  }

  updatePlaceholder() {
    if (this.files.size === 0) {
      this.targetPlaceholderTarget.style.display = "flex"
    } else {
      this.targetPlaceholderTarget.style.display = "none"
    }
  }

  createPreviewCard(file) {
    const card = document.createElement("div")
    card.className = "preview-card relative bg-gray-800 rounded-lg overflow-hidden m-2"
    card.dataset.fileName = file.name
    card.style.width = "150px"
    card.style.height = "150px"

    const preview = document.createElement("div")
    preview.className = "w-full h-full"
    
    const removeButton = document.createElement("button")
    removeButton.className = "absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-lg"
    removeButton.innerHTML = "×"
    removeButton.addEventListener("click", (e) => this.removeFile(e))

    const fileName = document.createElement("div")
    fileName.className = "absolute bottom-0 left-0 right-0 bg-gray-800 bg-opacity-75 text-white p-2 text-xs truncate"
    fileName.textContent = file.name

    // Add duration badge for videos and GIFs
    if (file.type.startsWith("video/") || file.type === "image/gif") {
      const duration = this.fileMetadata.get(file.name)?.duration
      if (duration) {
        const durationBadge = document.createElement("div")
        durationBadge.className = "absolute top-2 left-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded"
        durationBadge.textContent = `${Math.ceil(duration)}s`
        card.appendChild(durationBadge)
      }
    }

    card.appendChild(preview)
    card.appendChild(removeButton)
    card.appendChild(fileName)
    this.targetContainerTarget.appendChild(card)

    this.loadPreview(file, preview)
  }

  loadPreview(file, element) {
    if (file.type.startsWith("video/")) {
      const video = document.createElement("video")
      video.preload = "metadata"
      const objectUrl = URL.createObjectURL(file)
      
      video.addEventListener("loadeddata", () => {
        video.currentTime = 0
      })

      video.addEventListener("seeked", () => {
        const canvas = document.createElement("canvas")
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight
        
        const ctx = canvas.getContext("2d")
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
        
        element.style.backgroundImage = `url('${canvas.toDataURL("image/jpeg", 0.8)}')`
        element.style.backgroundSize = "cover"
        element.style.backgroundPosition = "center"
        
        URL.revokeObjectURL(objectUrl)
      })

      video.src = objectUrl
    } else {
      const reader = new FileReader()
      reader.onload = (e) => {
        element.style.backgroundImage = `url('${e.target.result}')`
        element.style.backgroundSize = "cover"
        element.style.backgroundPosition = "center"
      }
      reader.readAsDataURL(file)
    }
  }

  dispatchFileType(fileType) {
    const event = new CustomEvent("upscale:filetype", {
      detail: { fileType }
    })
    window.dispatchEvent(event)
  }

  async dispatchCostCalculation() {
    // Ensure all metadata is loaded before dispatching
    const filesWithMetadata = await Promise.all(
      Array.from(this.files).map(async (file) => {
        let metadata = this.fileMetadata.get(file.name)

        // If metadata doesn't exist yet, process it
        if (!metadata && (file.type === "image/gif" || file.type.startsWith("video/"))) {
          const duration = file.type === "image/gif" 
            ? await this.getGifDuration(file)
            : await this.getVideoDuration(file)
          metadata = { duration }
          this.fileMetadata.set(file.name, metadata)
        }

        return {
          file,
          metadata: metadata || {}
        }
      })
    )

    const event = new CustomEvent("files:changed", {
      detail: { files: filesWithMetadata },
      bubbles: true
    })
    window.dispatchEvent(event)
  }

  handleDragEnter(e) {
    e.preventDefault()
    e.stopPropagation()
  }

  handleDragLeave(e) {
    e.preventDefault()
    e.stopPropagation()
  }

  handleDragStart(e) {
    e.preventDefault()
    e.stopPropagation()
    return false
  }

  resetBasePhoto() {
    // Reset base photo dropzone
    this.basePhotoTarget.style.backgroundImage = "none"
    this.baseFileNameTarget.style.display = "none"
    this.baseFileNameTarget.textContent = ""
    // Dispatch event for base-photo-selector to handle its own placeholder
    const event = new CustomEvent('base-photo:reset', { bubbles: true })
    this.basePhotoTarget.dispatchEvent(event)
  }

  showError(message) {
    // Find the form
    const form = this.element.closest('form')
    if (!form) return
    
    // Find or create error container for base photo
    let errorContainer = form.querySelector('.base-photo-errors')
    if (!errorContainer) {
      // Find the base photo section
      const basePhotoSection = this.basePhotoTarget.closest('.mb-6')
      if (basePhotoSection) {
        // Create error container if it doesn't exist
        errorContainer = document.createElement('div')
        errorContainer.className = 'base-photo-errors text-red-500 mt-2'
        // Insert after the label but before the dropzone
        const labelContainer = basePhotoSection.querySelector('.flex.items-center.mb-2')
        if (labelContainer && labelContainer.nextSibling) {
          labelContainer.parentNode.insertBefore(errorContainer, labelContainer.nextSibling)
        }
      }
    }
    
    if (errorContainer) {
      // Create persistent error message (like Rails form errors)
      const errorP = document.createElement('p')
      errorP.className = 'text-sm text-red-500 mt-1'
      errorP.textContent = message
      
      // Clear existing errors and add new one
      errorContainer.innerHTML = ''
      errorContainer.appendChild(errorP)
      
      // Also show a toaster notification with longer timeout
      this.showToaster(message)
    }
  }

  showToaster(message) {
    // Create toaster notification
    const toaster = document.createElement('div')
    toaster.className = 'fixed top-4 right-4 z-50 bg-red-900 bg-opacity-90 border border-red-700 text-red-100 px-6 py-4 rounded-lg shadow-lg max-w-md'
    
    // Create inner structure safely without innerHTML
    const container = document.createElement('div')
    container.className = 'flex items-start'
    
    const textContainer = document.createElement('div')
    textContainer.className = 'flex-1'
    
    const title = document.createElement('p')
    title.className = 'text-sm font-medium'
    title.textContent = 'Validation Error'
    
    const messageP = document.createElement('p')
    messageP.className = 'text-sm mt-1'
    messageP.textContent = message // Safe from XSS
    
    textContainer.appendChild(title)
    textContainer.appendChild(messageP)
    
    const closeButton = document.createElement('button')
    closeButton.className = 'ml-4 text-red-300 hover:text-red-100'
    closeButton.innerHTML = `
      <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
      </svg>
    `
    
    // Bind click handler properly (no inline onclick)
    closeButton.addEventListener('click', () => toaster.remove())
    
    container.appendChild(textContainer)
    container.appendChild(closeButton)
    toaster.appendChild(container)
    
    document.body.appendChild(toaster)
    
    // Auto-remove after 10 seconds (longer timeout)
    setTimeout(() => {
      if (toaster.parentElement) {
        toaster.remove()
      }
    }, 10000)
  }

  clearErrors() {
    // Clear form errors
    const form = this.element.closest('form')
    if (form) {
      const errorContainer = form.querySelector('.base-photo-errors')
      if (errorContainer) {
        errorContainer.innerHTML = ''
      }
    }
  }
}
